/**
 * Who may see what (CR 107-109, 128-130, 422.4, 425, 437; research 02 §12.1; docs/design/05 §5).
 *
 * Two questions per object and viewer:
 * - may they see the object's **id** (know it exists as that individual object)?
 * - may they see its **face** (`cardId`, `printingId`)?
 *
 * | Zone | Id visible to | Face visible to |
 * |---|---|---|
 * | public zones (board, trash, banishment, champion, legend, chain) | everyone | everyone |
 * | facedown (CR 107.3.f) | everyone (a facedown card is there) | its controller; everyone once the game ends (CR 422.4) |
 * | hand (CR 108.7.c) | its owner | its owner |
 * | Main Deck, Rune Deck (CR 108.4.e, 108.5.d) | nobody | nobody |
 *
 * plus `revealedTo` grants on top (revealed cards, cards a player looked at). Spectators only ever see
 * what everyone sees. `viewFor`, `eventsFor` and every command's object lookup use these functions, so
 * the rules for "hidden" are written once.
 */
import type { GameState } from '../state/game-state';
import { type Audience, type PlayerId, inAudience } from '../state/ids';
import type { GameObject } from '../state/objects';
import { ZONE_INFO } from '../state/zones';

export type Viewer = PlayerId | 'spectator';

function withGrants(base: PlayerId[], grants: Audience): Audience {
  if (grants === 'all') return 'all';
  const out = base.slice();
  for (const p of grants) if (!out.includes(p)) out.push(p);
  return out;
}

/** Who may see that this individual object exists (its id). */
export function idAudience(_state: GameState, object: GameObject): Audience {
  switch (object.zone.kind) {
    case 'hand':
      return withGrants([object.owner], object.revealedTo);
    case 'mainDeck':
    case 'runeDeck':
      return withGrants([], object.revealedTo);
    case 'facedown':
    case 'trash':
    case 'banishment':
    case 'champion':
    case 'legend':
    case 'base':
    case 'runes':
    case 'battlefield':
    case 'chain':
      return 'all';
  }
}

/** Who may see the object's face (card identity and art). */
export function cardAudience(state: GameState, object: GameObject): Audience {
  switch (object.zone.kind) {
    case 'hand':
      return withGrants([object.owner], object.revealedTo);
    case 'mainDeck':
    case 'runeDeck':
      return withGrants([], object.revealedTo);
    case 'facedown':
      // Every facedown card is revealed when the game ends (CR 422.4).
      return state.result !== null ? 'all' : withGrants([object.controller], object.revealedTo);
    case 'trash':
    case 'banishment':
    case 'champion':
    case 'legend':
    case 'base':
    case 'runes':
    case 'battlefield':
    case 'chain':
      return 'all';
  }
}

/** Whether `viewer` may see that this object exists. */
export function canSeeId(state: GameState, object: GameObject, viewer: Viewer): boolean {
  return inAudience(idAudience(state, object), viewer);
}

/** Whether `viewer` may see this object's face. */
export function canSeeCard(state: GameState, object: GameObject, viewer: Viewer): boolean {
  return inAudience(cardAudience(state, object), viewer);
}

/** Whether the object is in a zone whose contents are hidden from some players by default. */
export function isInHiddenZone(object: GameObject): boolean {
  return ZONE_INFO[object.zone.kind].privacy !== 'public';
}
